/**
  * This is an abstract directive to be used in various
  * components related to technical drawings. It handles
  * svg interactiveness. It is a directive because it has
  * no template. It will only be used a an inherited class.
 */
import {
  Directive,
  ElementRef,
  OnDestroy
} from '@angular/core';

@Directive()
export abstract class HaynesProSvgInteractionDirective implements OnDestroy {
  protected svgElement               : SVGSVGElement | null              = null;

  protected svgElementCache          : Record<number, Array<Element>>    = {};

  protected svgInteractionTargetCache: Record<number, Array<SVGElement>> = {};

  private readonly drawableTags = [
    'circle',
    'ellipse',
    'line',
    'path',
    'polygon',
    'polyline',
    'rect',
    'use'
  ];

  private readonly CSS_CLASSES = {
    HOVER   : 'hp-td-part--hover',
    PART    : 'hp-td-part',
    SELECTED: 'hp-td-part--selected'
  } as const;

  private readonly CURSOR = {
    DEFAULT: 'default',
    POINTER: 'pointer'
  } as const;

  protected abstract getHoveredItemId()                         : number | null;
  protected abstract getInteractiveItemIds()                    : Array<number>;
  protected abstract getMatchedSvgElements ( itemId : number )  : Array<Element>;
  protected abstract getSelectedItemId()                        : number | null;
  protected abstract handleResolvedSvgClick ( itemId : number ) : void;
  protected abstract setHoveredItemId ( itemId : number | null ): void;

  ngOnDestroy () : void {
    this.unbindSvgPointerDelegation();
    this.svgElement                = null;
    this.svgElementCache           = {};
    this.svgInteractionTargetCache = {};
  }

  protected applySvgState () : void {
    const selectedItemId = this.getSelectedItemId();
    const hoveredItemId  = this.getHoveredItemId();

    if (!this.svgElement) {
      return;
    }

    this.clearSvgState();

    if (selectedItemId) {
      this.getMatchedSvgElements(selectedItemId)
      .forEach(( element : Element ) => element.classList.add(this.CSS_CLASSES.SELECTED));
    }

    if (hoveredItemId && hoveredItemId !== selectedItemId) {
      this.getMatchedSvgElements(hoveredItemId)
      .forEach(( element : Element ) => element.classList.add(this.CSS_CLASSES.HOVER));
    }
  }

  protected bindSvgInteractions () : void {
    this.getInteractiveItemIds()
    .forEach(( itemId : number ) => {
      if (!itemId) {
        return;
      }

      this.getSvgInteractionElements(itemId)
      .forEach(( element : SVGElement ) => {
        const svgElement = element as SVGElement & {
          __hpBound?: boolean;
        };

        if (svgElement.__hpBound) {
          return;
        }

        svgElement.__hpBound = true;
        svgElement.classList.add(this.CSS_CLASSES.PART);
        svgElement.style.pointerEvents = 'visible';
        svgElement.style.cursor        = this.CURSOR.POINTER;
      });
    });
  }

  protected bindSvgPointerDelegation () : void {
    if (!this.svgElement) {
      return;
    }

    this.svgElement.addEventListener('pointermove', this.svgPointerMoveHandler);
    this.svgElement.addEventListener('pointerleave', this.svgPointerLeaveHandler);
    this.svgElement.addEventListener('click', this.svgClickHandler);
  }

  protected clearSvgState () : void {
    if (!this.svgElement) {
      return;
    }

    this.svgElement.querySelectorAll(`.${this.CSS_CLASSES.HOVER}, .${this.CSS_CLASSES.SELECTED}`)
    .forEach(( element : Element ) => {
      element.classList.remove(this.CSS_CLASSES.HOVER);
      element.classList.remove(this.CSS_CLASSES.SELECTED);
    });
  }

  protected getSvgPoint (
    clientX : number,
    clientY : number
  ) : DOMPoint | null {
    if (!this.svgElement) {
      return null;
    }

    const ctm = this.svgElement.getScreenCTM();

    if (!ctm) {
      return null;
    }

    return new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
  }

  protected getGeometryHitScore (
    element : SVGGeometryElement,
    point   : DOMPoint
  ) : number {
    try {
      if (typeof element.isPointInFill === 'function' && element.isPointInFill(point)) {
        return 2;
      }

      if (typeof element.isPointInStroke === 'function' && element.isPointInStroke(point)) {
        return 1;
      }
    } catch {
      return 0;
    }

    return 0;
  }

  protected getGeometryArea ( element : SVGElement ) : number {
    const graphicsElement = element as SVGGraphicsElement;

    if (typeof graphicsElement.getBBox !== 'function') {
      return Number.MAX_SAFE_INTEGER;
    }

    try {
      const box = graphicsElement.getBBox();

      if (!box || (!box.width && !box.height)) {
        return Number.MAX_SAFE_INTEGER;
      }

      return box.width * box.height;
    } catch {
      return Number.MAX_SAFE_INTEGER;
    }
  }

  protected getSvgInteractionTargets ( element : Element ) : Array<SVGElement> {
    const tagName = element.tagName.toLowerCase();

    if (this.drawableTags.includes(tagName)) {
      return [ element as SVGElement ];
    }

    if (tagName === 'g') {
      return Array.from(
        element.querySelectorAll(this.drawableTags.join(', ') as string)
      ) as Array<SVGElement>;
    }

    return [];
  }

  protected getSvgInteractionElements ( itemId : number ) : Array<SVGElement> {
    if (!itemId) {
      return [];
    }

    if (!this.svgInteractionTargetCache[itemId]) {
      this.svgInteractionTargetCache[itemId] = this.getMatchedSvgElements(itemId)
      .flatMap(( element : Element ) => this.getSvgInteractionTargets(element));
    }

    return this.svgInteractionTargetCache[itemId];
  }

  protected handleSvgPointerMove ( event : PointerEvent ) : void {
    const itemId = this.resolveItemIdFromPoint(event.clientX, event.clientY);

    if (this.svgElement) {
      this.svgElement.style.cursor = itemId ? this.CURSOR.POINTER : this.CURSOR.DEFAULT;
    }

    if (this.getHoveredItemId() !== itemId) {
      this.setHoveredItemId(itemId);
      this.applySvgState();
    }
  }

  protected handleSvgPointerLeave () : void {
    if (this.svgElement) {
      this.svgElement.style.cursor = this.CURSOR.DEFAULT;
    }

    if (this.getHoveredItemId() !== null) {
      this.setHoveredItemId(null);
      this.applySvgState();
    }
  }

  protected handleSvgClick ( event : MouseEvent ) : void {
    const itemId = this.resolveItemIdFromPoint(event.clientX, event.clientY);

    if (!itemId) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.handleResolvedSvgClick(itemId);
  }

  protected isPointNearGeometryBox (
    element : SVGElement,
    x       : number,
    y       : number
  ) : boolean {
    const graphicsElement = element as SVGGraphicsElement;

    if (typeof graphicsElement.getBBox !== 'function') {
      return true;
    }

    try {
      const box     = graphicsElement.getBBox();
      const padding = 2;

      return x >= (box.x - padding)
        && x <= (box.x + box.width + padding)
        && y >= (box.y - padding)
        && y <= (box.y + box.height + padding);
    } catch {
      return true;
    }
  }

  protected injectInlineSvg (
    svgText      : string,
    svgContainer : ElementRef<HTMLDivElement>
  ) : void {
    const parsedDocument = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    const svgElement     = parsedDocument.documentElement as unknown as SVGSVGElement;

    svgElement.removeAttribute('width');
    svgElement.removeAttribute('height');
    svgElement.style.width  = '100%';
    svgElement.style.height = 'auto';

    svgContainer.nativeElement.innerHTML = '';
    svgContainer.nativeElement.appendChild(svgElement);

    this.unbindSvgPointerDelegation();

    this.svgElement                = svgElement;
    this.svgElementCache           = {};
    this.svgInteractionTargetCache = {};

    this.bindSvgInteractions();
    this.bindSvgPointerDelegation();
  }

  protected resolveItemIdFromPoint ( clientX : number, clientY : number ) : number | null {
    if (!this.svgElement) {
      return null;
    }

    const svgPoint = this.getSvgPoint(clientX, clientY);

    if (!svgPoint) {
      return null;
    }

    const matches : Array<{
      itemId : number;
      area   : number;
      score  : number;
    }> = [];

    this.getInteractiveItemIds()
    .forEach(( itemId : number ) => {
      if (!itemId) {
        return;
      }

      const targets = this.getSvgInteractionElements(itemId);

      let bestScore = 0;
      let bestArea  = Number.MAX_SAFE_INTEGER;

      targets.forEach(( target : SVGElement ) => {
        const geometry = target as SVGGeometryElement;

        if (!geometry.isPointInFill && !geometry.isPointInStroke) {
          return;
        }

        if (!this.isPointNearGeometryBox(target, svgPoint.x, svgPoint.y)) {
          return;
        }

        const hitScore = this.getGeometryHitScore(geometry, svgPoint);

        if (!hitScore) {
          return;
        }

        const area = this.getGeometryArea(target);

        if (hitScore > bestScore || (hitScore === bestScore && area < bestArea)) {
          bestScore = hitScore;
          bestArea  = area;
        }
      });

      if (bestScore) {
        matches.push({
          itemId,
          area : bestArea,
          score: bestScore
        });
      }
    });

    if (!matches.length) {
      return null;
    }

    matches.sort(( a, b ) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      return a.area - b.area;
    });

    return matches[0].itemId;
  }

  protected svgPointerMoveHandler = ( event : PointerEvent ) => {
    this.handleSvgPointerMove(event);
  };

  protected svgPointerLeaveHandler = () => {
    this.handleSvgPointerLeave();
  };

  protected svgClickHandler = ( event : MouseEvent ) => {
    this.handleSvgClick(event);
  };

  protected unbindSvgPointerDelegation () : void {
    if (!this.svgElement) {
      return;
    }

    this.svgElement.removeEventListener('pointermove', this.svgPointerMoveHandler);
    this.svgElement.removeEventListener('pointerleave', this.svgPointerLeaveHandler);
    this.svgElement.removeEventListener('click', this.svgClickHandler);
  }
}